import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "../axios-orders";
import { queryKeys } from "./queryKeys";
import {
  DeliveryConfirmationResponse,
  DeliveryCountResponse,
  DeliveryInterface,
  DeliveryRequest,
  DeliveryState,
  DeliveryUpdateRequest,
  PaginatedDeliveryInterface,
} from "../types";

const onGetDeliveries = async (
  state: DeliveryState | undefined,
  page = 0,
  size = 10
) => {
  // No state = the "All" filter option -> the all-states endpoint; otherwise the per-state one.
  const url = state
    ? `/deliveries/state/${state}/${page}?size=${size}`
    : `/deliveries/all/${page}?size=${size}`;
  const { data } = await axios.get(url);
  return data;
};

export const useDeliveries = (
  state: DeliveryState | undefined,
  page = 0,
  size = 10
) =>
  useQuery<PaginatedDeliveryInterface>({
    queryKey: [queryKeys.DELIVERIES, page, state ?? "all", size],
    queryFn: () => onGetDeliveries(state, page, size),
    retry: 1,
  });

const onGetDelivery = async (deliveryId: string) => {
  const { data } = await axios.get(`/deliveries/${deliveryId}`);
  return data;
};

export const useDelivery = (deliveryId: string) =>
  useQuery<DeliveryInterface>({
    queryKey: [queryKeys.DELIVERY, deliveryId],
    queryFn: () => onGetDelivery(deliveryId),
    enabled: !!deliveryId,
    retry: 1,
  });

const onCreateDelivery = async (body: DeliveryRequest) => {
  const { data } = await axios.post("/deliveries", body);
  return data;
};

export const useCreateDelivery = (
  onSuccess: (res: any) => void,
  onError?: (error: unknown) => void
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: onCreateDelivery,
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: [queryKeys.DELIVERIES] });
      onSuccess(res);
    },
    onError,
  });
};

const onEditDelivery = async ({
  deliveryId,
  body,
}: {
  deliveryId: string;
  body: any;
}) => {
  const { data } = await axios.put(`/deliveries/${deliveryId}`, body);
  return data;
};

export const useEditDelivery = (
  onSuccess: (res: any) => void,
  onError?: () => void
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: onEditDelivery,
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: [queryKeys.DELIVERY] });
      onSuccess(res);
    },
    onError,
  });
};

const onUpdateDelivery = async ({
  deliveryId,
  endpoint,
}: DeliveryUpdateRequest) => {
  const { data } = await axios.post(`/deliveries/${deliveryId}/${endpoint}`);
  return data;
};

export const useUpdateDelivery = (
  onSuccess: (res: any) => void,
  onError?: () => void
) =>
  useMutation({
    mutationFn: onUpdateDelivery,
    onSuccess,
    onError,
  });

const onGetDeliveryByToken = async (body: {
  token: string;
  receiverComment?: string;
}) => {
  const { data } = await axios.post(`/deliveries/token/details`, body);
  return data;
};

export const useGetDeliveryByToken = (
  onSuccess: (res: any) => void,
  onError?: () => void
) =>
  useMutation<DeliveryConfirmationResponse, Error, { token: string }>({
    mutationFn: onGetDeliveryByToken,
    onSuccess,
    onError,
  });

const onConfirmDelivery = async (body: {
  token: string;
  receiverComment?: string;
  receptionDate?: string;
  remittanceNumber?: string;
  remittanceFile?: string;
}) => {
  const { data } = await axios.post(`/deliveries/confirm`, body);
  return data;
};

/**
 * Uploads a remito document from the supplier's (unauthenticated) token confirmation page. The
 * delivery token is the authorisation — it maps to the public `POST /deliveries/token/remito-file`
 * endpoint — and the returned S3 URL is sent back as `remittanceFile` on confirm.
 */
const onUploadRemitoFileByToken = async ({
  token,
  file,
}: {
  token: string;
  file: File;
}): Promise<{ url: string }> => {
  const formData = new FormData();
  formData.append("token", token);
  formData.append("file", file);
  const { data } = await axios.post(`/deliveries/token/remito-file`, formData);
  return data;
};

export const useUploadRemitoFileByToken = (
  onSuccess?: (data: { url: string }) => void,
  onError?: (error: unknown) => void
) =>
  useMutation({
    mutationFn: onUploadRemitoFileByToken,
    onSuccess,
    onError,
  });

export const useConfirmDelivery = (
  onSuccess: (res: any) => void,
  onError?: (error: unknown) => void
) => {
  const queryClient = useQueryClient();

  return useMutation<
    DeliveryConfirmationResponse,
    Error,
    {
      token: string;
      receiverComment?: string;
      receptionDate?: string;
      remittanceNumber?: string;
      remittanceFile?: string;
    }
  >({
    mutationFn: onConfirmDelivery,
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: [queryKeys.DELIVERY] });
      onSuccess(res);
    },
    onError,
  });
};

const onDownloadDeliveryTemplate = async () => {
  const response = await axios.get("/deliveries/template", {
    responseType: "blob",
  });
  return response;
};

export const useDownloadDeliveryTemplate = () => {
  return useMutation({
    mutationFn: onDownloadDeliveryTemplate,
  });
};

const onUploadDeliveryExcel = async (file: File) => {
  const formData = new FormData();
  formData.append("file", file);

  const { data } = await axios.post("/deliveries/upload", formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
  return data;
};

export const useUploadDeliveryExcel = (
  onSuccess: (res: any) => void,
  onError: (error: any) => void
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: onUploadDeliveryExcel,
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: [queryKeys.DELIVERIES] });
      onSuccess(res);
    },
    onError,
  });
};

const onUpdateDeliveryFileUrl = async ({
  deliveryId,
  fileUrl,
}: {
  deliveryId: string;
  fileUrl: string;
}) => {
  const { data } = await axios.put(
    `/deliveries/${deliveryId}/purchase-order-file`,
    {
      purchaseOrderFile: fileUrl,
    }
  );
  return data;
};

export const useUpdateDeliveryFileUrl = (
  onSuccess: (res: any) => void,
  onError?: (error: any) => void
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: onUpdateDeliveryFileUrl,
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: [queryKeys.DELIVERY] });
      queryClient.invalidateQueries({ queryKey: [queryKeys.DELIVERIES] });
      onSuccess(res);
    },
    onError,
  });
};

const onGetDeliveriesCount = async () => {
  const { data } = await axios.get("/deliveries/count-by-state");
  return data;
};

export const useGetDeliveriesCount = () =>
  useQuery<DeliveryCountResponse>({
    queryKey: [queryKeys.DELIVERIES_COUNT],
    queryFn: onGetDeliveriesCount,
  });

const onGetDeliveriesBySupplier = async (
  state: DeliveryState | undefined,
  page: number,
  size = 10
) => {
  // No state = "All" -> the all-states seller endpoint; otherwise the per-state seller one.
  const url = state
    ? `/deliveries/user/state/${state}/${page}?size=${size}`
    : `/deliveries/user/${page}?size=${size}`;
  const { data } = await axios.get(url);
  return data;
};

export const useDeliveriesBySupplier = (
  state: DeliveryState | undefined,
  page = 0,
  size = 10
) =>
  useQuery<PaginatedDeliveryInterface>({
    queryKey: [queryKeys.DELIVERIES, page, state ?? "all", size],
    queryFn: () => onGetDeliveriesBySupplier(state, page, size),
    retry: false,
  });
